import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BookOpen, Plus, CalendarIcon, Trash2, MoreVertical, Sparkles, Compass, Eraser } from 'lucide-react';
import { toast } from 'sonner';
import { format, isSameDay } from 'date-fns';
import { JournalSkeleton } from '@/components/Skeletons';

const MOODS = [
  { value: 'aligned', label: 'Aligned', color: 'bg-moss/20 text-moss' },
  { value: 'expanding', label: 'Expanding', color: 'bg-gold/20 text-gold' },
  { value: 'reflecting', label: 'Reflecting', color: 'bg-dark/10 text-dark' },
  { value: 'resistant', label: 'Resistant', color: 'bg-red-100 text-red-600' },
  { value: 'grateful', label: 'Grateful', color: 'bg-gold/30 text-dark' },
];

// Journal Templates
const TEMPLATES = {
  decoding: {
    id: 'decoding',
    label: 'Decoding a Pattern',
    stage: 'When you want to understand a stuck situation',
    icon: Compass,
    description: 'Trace something that happened back to what it really means and what part of you might be choosing it.',
    fields: [
      { key: 'event', label: 'What happened?', placeholder: 'A situation, conversation, or pattern you noticed. Be brief and specific.' },
      { key: 'meaning', label: 'What did you make it mean?', placeholder: 'What did this seem to confirm about you, others, or how life works?' },
      { key: 'hidden_yes', label: 'If a part of you was choosing this, what would it be getting?', placeholder: 'Safety, identity, belonging, control, being right, being seen... what is the hidden payoff?' },
    ],
    format: ({ event, meaning, hidden_yes }) =>
      `[Decoding a Pattern]\n\nWhat happened:\n${event}\n\nWhat I made it mean:\n${meaning}\n\nThe hidden payoff:\n${hidden_yes}`,
  },
  scrubbing: {
    id: 'scrubbing',
    label: 'Clearing Old Guilt',
    stage: 'When old shame or self-judgment surfaces',
    icon: Eraser,
    description: 'Forgive yourself for the misunderstanding. Name the truth.',
    fields: [
      { key: 'misunderstanding', label: 'I forgive myself for misunderstanding that...', placeholder: 'Name the false belief or self-judgment plainly.' },
      { key: 'truth', label: 'The truth is...', placeholder: 'State the clean truth in present tense — no qualifiers, no defense.' },
    ],
    format: ({ misunderstanding, truth }) =>
      `[Clearing Old Guilt]\n\nI forgive myself for misunderstanding that:\n${misunderstanding}\n\nThe truth is:\n${truth}`,
  },
};

// Welcome examples shown ONLY when the user has zero journal entries.
// Two real samples from Rachelle — not editable, not stored, just inspiration
// for the kind of inner work this journal is built to hold.
const WELCOME_EXAMPLES = [
  {
    date: 'October 12, 2026',
    state: 'Feeling a bit restless.',
    body: `I got distracted today. I saw a picture of my person online and my heart started racing. I immediately felt the old fear that I'm being replaced or forgotten. I felt a huge push to check up on them or send a text just to see if they'd answer. But I noticed the tension inside and I stopped myself. Instead, I chose to calm my body and come to grounded presence instead. I just sat for two minutes and focused on my center, breathing into the tension and then feeling love for the anxious part of myself, until the panic went away.

I realized my fear was just trying to keep me safe by looking for 'bad news' in the world around me. But what I see right now is just the past. It's just an old shadow of who I used to be. I'm learning to embrace those feelings of uncertainty and stay calm and connect to a part of me that already knows.

I saw a small sign today: A stranger was really kind to me for no reason. It reminded me that my energy is contagious and staying grounded is starting to reflect back to me in my life. I'm choosing to stay connected to a part of me that already knows. I am already loved. It is done.`,
  },
  {
    date: 'October 14, 2026',
    state: 'A little overwhelmed by work.',
    body: `I've been in a heavy squeeze all week about my new business. Every time I don't see a new client email, I start worrying about my bank account. My Fear is telling me that if this doesn't work, I'm a failure and I'll be stuck forever.

I decided to reflect and inquire this morning to see what was really going on. I listened to my inner story and found that a part of me is actually scared of being successful. My fear thinks that if I make more money, I'll lose my free time or my friends will treat me differently. It was trying to protect me.

I used my tools to meet that part of me and calm my body. Then work with that old rule in my head. I chose to forgive myself for the mistake of thinking I wasn't ready or capable. I'm letting go of the struggle tonight. I'm prioritizing calming my nervous system and moving forward with ease instead of attachment and fear.

I trust what my intuition is telling me in those calm moments. I am a leader, and my only job is to stay calm and connected enough to hear what to do next. My life will catch up. I'm taking one step at a time.`,
  },
];

export default function JournalPage() {
  const [entries, setEntries] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const [content, setContent] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [isWriting, setIsWriting] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null); // 'free' | 'decoding' | 'scrubbing'
  const [templateFields, setTemplateFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([loadEntries(), loadPrompt()]).finally(() => setLoading(false));
  }, []);

  const loadEntries = async () => {
    try {
      const res = await api.get('/journal');
      setEntries(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadPrompt = async () => {
    try {
      const res = await api.get('/journal/prompt');
      setPrompt(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const startWriting = (templateId) => {
    setActiveTemplate(templateId);
    setTemplateFields({});
    setContent('');
    setSelectedMood('');
    setIsWriting(true);
  };

  const saveEntry = async () => {
    let finalContent = content.trim();
    let finalPrompt = prompt?.prompt;

    if (activeTemplate && activeTemplate !== 'free') {
      const tpl = TEMPLATES[activeTemplate];
      const allFilled = tpl.fields.every(f => (templateFields[f.key] || '').trim());
      if (!allFilled) return;
      finalContent = tpl.format(
        Object.fromEntries(tpl.fields.map(f => [f.key, (templateFields[f.key] || '').trim()]))
      );
      finalPrompt = tpl.label;
    }

    if (!finalContent) return;
    setSaving(true);
    try {
      const res = await api.post('/journal', {
        content: finalContent,
        prompt: finalPrompt,
        mood: selectedMood || null,
      });
      setEntries(prev => [res.data, ...prev]);
      setContent('');
      setSelectedMood('');
      setTemplateFields({});
      setActiveTemplate(null);
      setIsWriting(false);
      toast.success('Reflection saved');
    } catch (err) {
      toast.error('Could not save entry');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      await api.delete(`/journal/${entryId}`);
      setEntries(prev => prev.filter(e => e.entry_id !== entryId));
      toast.success('Entry removed');
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  const filteredEntries = selectedDate
    ? entries.filter(e => isSameDay(new Date(e.created_at), selectedDate))
    : entries;

  const entryDates = entries.map(e => new Date(e.created_at));
  const tpl = activeTemplate && activeTemplate !== 'free' ? TEMPLATES[activeTemplate] : null;
  const templateValid = tpl ? tpl.fields.every(f => (templateFields[f.key] || '').trim()) : true;

  if (loading) return <JournalSkeleton />;

  return (
    <div className="space-y-8" data-testid="journal-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 animate-fade-up">
        <div>
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Inner Reflection</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading">Journal</h1>
        </div>
        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="calendar-filter-button"
                className="rounded-xl border-black/10 bg-bone/40 hover:bg-bone font-sans text-sm"
              >
                <CalendarIcon size={16} className="mr-2" />
                {selectedDate ? format(selectedDate, 'MMM d') : 'Filter by date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-bone border-black/10 rounded-2xl" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => setSelectedDate(date === selectedDate ? null : date)}
                modifiers={{ hasEntry: entryDates }}
                modifiersStyles={{ hasEntry: { fontWeight: 700, textDecoration: 'underline', textDecorationColor: '#C6B28F' } }}
                className="rounded-2xl"
              />
            </PopoverContent>
          </Popover>
          {selectedDate && (
            <Button
              variant="ghost"
              onClick={() => setSelectedDate(null)}
              className="text-text-muted text-sm font-sans"
              data-testid="clear-date-filter"
            >
              Clear filter
            </Button>
          )}
        </div>
      </div>

      {/* Daily Prompt + Template Picker */}
      {!isWriting ? (
        <div className="space-y-4 animate-fade-up delay-100">
          <div className="mystical-card p-8" data-testid="journal-prompt-section">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-gold" />
              <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Today's Prompt</span>
            </div>
            <p className="font-serif text-xl sm:text-2xl text-dark leading-relaxed italic mb-6">
              "{prompt?.prompt || 'Loading...'}"
            </p>
            <Button
              onClick={() => startWriting('free')}
              data-testid="begin-writing-button"
              className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
            >
              <Plus size={16} className="mr-2" /> Free-form Reflection
            </Button>
          </div>

          {/* SAP Templates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="journal-templates">
            {Object.values(TEMPLATES).map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => startWriting(t.id)}
                  data-testid={`template-${t.id}`}
                  className="mystical-card p-6 text-left group hover:border-gold/30"
                  style={{ transition: 'border-color 0.3s ease, box-shadow 0.3s ease' }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={16} className="text-gold" />
                    <span className="text-gold font-sans text-[10px] tracking-[0.3em] uppercase">{t.stage}</span>
                  </div>
                  <p className="font-serif text-xl text-dark mb-2">{t.label}</p>
                  <p className="font-sans text-sm text-text-muted leading-relaxed">{t.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="glass-card-solid p-8 animate-fade-up" data-testid="journal-writing-area">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={16} className="text-moss" />
            <span className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">
              {tpl ? tpl.label : 'Your Reflection'}
            </span>
          </div>

          {tpl ? (
            <div className="space-y-5 mb-4">
              <p className="font-serif text-base text-text-muted italic">{tpl.stage} · {tpl.description}</p>
              {tpl.fields.map((f) => (
                <div key={f.key}>
                  <label className="text-text-muted text-xs tracking-wider uppercase font-sans block mb-2">{f.label}</label>
                  <Textarea
                    value={templateFields[f.key] || ''}
                    onChange={(e) => setTemplateFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    data-testid={`template-field-${f.key}`}
                    rows={3}
                    className="bg-transparent border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans text-base leading-relaxed resize-none"
                  />
                </div>
              ))}
            </div>
          ) : (
            <>
              {prompt?.prompt && (
                <p className="font-serif text-lg text-text-muted italic mb-4">"{prompt.prompt}"</p>
              )}
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Let your thoughts flow freely..."
                data-testid="journal-content-input"
                className="min-h-[200px] bg-transparent border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans text-base leading-relaxed resize-none mb-4"
              />
            </>
          )}

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-text-muted font-sans text-xs tracking-wider uppercase mr-1">Mood:</span>
              {MOODS.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => setSelectedMood(mood.value === selectedMood ? '' : mood.value)}
                  data-testid={`mood-${mood.value}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-sans tracking-wider ${
                    selectedMood === mood.value ? mood.color + ' ring-1 ring-current' : 'bg-sand text-text-muted'
                  }`}
                  style={{ transition: 'background-color 0.2s ease, color 0.2s ease' }}
                >
                  {mood.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => { setIsWriting(false); setContent(''); setSelectedMood(''); setActiveTemplate(null); setTemplateFields({}); }}
                className="font-sans text-sm text-text-muted"
                data-testid="cancel-writing-button"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEntry}
                disabled={(tpl ? !templateValid : !content.trim()) || saving}
                data-testid="save-journal-button"
                className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
              >
                {saving ? 'Saving...' : 'Save Reflection'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="space-y-4">
        {filteredEntries.length === 0 ? (
          selectedDate ? (
            <div className="text-center py-16 animate-fade-up">
              <BookOpen size={36} className="mx-auto text-text-muted/30 mb-4" />
              <p className="font-serif text-xl text-text-muted">No entries on this date</p>
            </div>
          ) : (
            <div className="space-y-5 animate-fade-up" data-testid="journal-welcome-examples">
              <div className="text-center py-2">
                <BookOpen size={28} className="mx-auto text-gold/60 mb-3" />
                <p className="font-serif text-xl text-dark mb-2">Your journal awaits its first words</p>
                <p className="font-sans text-sm text-text-muted max-w-xl mx-auto leading-relaxed">
                  Here are two reflections from Rachelle to show you the shape of this work — not a template to follow, just an invitation to find your own voice.
                </p>
              </div>
              {WELCOME_EXAMPLES.map((ex, i) => (
                <div
                  key={i}
                  className="mystical-card p-6 lg:p-8 border-gold/20 bg-gradient-to-br from-bone via-bone to-gold/5"
                  data-testid={`journal-welcome-example-${i}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={14} className="text-gold" />
                    <span className="text-gold font-sans text-[10px] tracking-[0.3em] uppercase">Sample · Rachelle</span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
                    <span className="font-serif text-base text-dark">{ex.date}</span>
                    <span className="font-sans text-xs text-text-muted italic">{ex.state}</span>
                  </div>
                  <p className="font-sans text-base text-dark leading-relaxed whitespace-pre-line">
                    {ex.body}
                  </p>
                </div>
              ))}
              <p className="text-center font-sans text-xs text-text-muted/70 italic pt-2">
                When you're ready, begin with the prompt above — or pick a template that meets where you are.
              </p>
            </div>
          )
        ) : (
          filteredEntries.map((entry, i) => {
            const mood = MOODS.find(m => m.value === entry.mood);
            return (
              <div
                key={entry.entry_id}
                className="glass-card p-6 lg:p-8 animate-fade-up"
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`journal-entry-${entry.entry_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-text-muted font-sans text-xs">
                      {format(new Date(entry.created_at), 'EEEE, MMMM d, yyyy')}
                    </span>
                    {mood && (
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-sans ${mood.color}`}>
                        {mood.label}
                      </span>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-text-muted hover:text-dark p-1" data-testid={`entry-menu-${entry.entry_id}`}>
                        <MoreVertical size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-bone border-black/10 rounded-xl">
                      <DropdownMenuItem
                        onClick={() => deleteEntry(entry.entry_id)}
                        className="text-red-500 focus:text-red-600 font-sans text-sm"
                        data-testid={`delete-entry-${entry.entry_id}`}
                      >
                        <Trash2 size={14} className="mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {entry.prompt && (
                  <p className="font-serif text-sm text-text-muted italic mb-3">Prompt: "{entry.prompt}"</p>
                )}
                <p className="font-sans text-base text-dark leading-relaxed whitespace-pre-wrap">
                  {entry.content}
                </p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
